import React, { useState, useEffect } from "react";
import {
  analyticsBusArrivalApi,
  analyticsRouteWiseBusOccupancy,
} from "../../services/AnalyticService";
import { Spinner } from "react-bootstrap";
import { dateRangeOptions } from "../../utilities";
import Chart from "react-apexcharts";
import {
  addDays,
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
} from "date-fns";
import DatePicker from "react-datepicker";
import Select from "react-select";
const BusArrivalCount = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedOption, setSelectedOption] = useState(dateRangeOptions[1]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [param, setParam] = useState({
    start_date: format(subDays(new Date(), 7), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
  });
  useEffect(() => {
    setLoading(true);
    analyticsBusArrivalApi(param)
      .then((res) => {
        setData(res?.data?.results || []);
        setError("");
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [param]);
  const handleSelectChange = (option) => {
    setSelectedOption(option);
    if (!option) {
      setParam({ ...param, start_date: "", end_date: "", page: "1" });
      return;
    }
    let startDate,
      endDate = new Date();
    switch (option.value) {
      case "yesterday":
        startDate = subDays(new Date(), 1);
        endDate = startDate;
        break;
      case "7_days":
        startDate = subDays(new Date(), 7);
        break;
      case "15_days":
        startDate = subDays(new Date(), 15);
        break;
      case "30_days":
        startDate = subDays(new Date(), 30);
        break;
      case "last_month":
        startDate = startOfMonth(subMonths(new Date(), 1));
        endDate = endOfMonth(subMonths(new Date(), 1));
        break;
      case "3_months":
        startDate = startOfMonth(subMonths(new Date(), 3));
        endDate = endOfMonth(subMonths(new Date(), 1));
        break;
      case "custom":
        setShowDatePicker(true);
        return;
      default:
        return;
    }
    setShowDatePicker(false);
    setParam({
      ...param,
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      page: "1",
    });
  };

  const fullLabels = data.map((item) => item.route_name);
  const routeNames = data.map((item) =>
    item.route_name?.length > 8
      ? item.route_name.slice(0, 8) + "..."
      : item.route_name
  );
  const statuses = [
    "On the Way",
    "Reached On Time",
    "Reached Delayed",
    "Reached Early",
    "Not Reached",
  ];
  const series = statuses.map((status) => ({
    name: status,
    data: data.map((route) => route[status]),
  }));
  const colors = ["#1E88E5", "#43A047", "#FB8C00", "#E53935", "#8E24AA"];

  // const colors = [
  //   "#4E342E", // Dark Cocoa
  //   "#6D4C41", // Mocha Brown
  //   "#8D6E63", // Soft Milk Chocolate
  //   "#A1887F", // Light Mocha
  //   "#D7CCC8"  // Creamy Latte
  // ];

  const options = {
    chart: {
      type: "bar",
      height: 400,
      stacked: true,
      stackType: "100%",
      toolbar: { show: false },
    },
    colors: colors, // ✅ only here
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%",
        borderRadius: 6,
      },
    },
    stroke: {
      show: true,
      width: 1,
      colors: ["#ffffff"],
    },
    xaxis: {
      categories: routeNames,
      labels: {
        style: {
          fontSize: "11px",
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => val + "%",
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const fullRoute = fullLabels[dataPointIndex];
        const statusName = w.globals.seriesNames[seriesIndex];
        const val = series[seriesIndex][dataPointIndex];

        return `
        <div style="
          background-color: #3e2723;
          color: #fff;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 13px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          <div style="font-weight: bold; font-size: 14px;">${statusName}</div>
          <div><span style="color: #d7ccc8;">Route:</span> ${fullRoute}</div>
          <div><span style="color: #d7ccc8;">Count:</span> ${val}</div>
        </div>
      `;
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
    },
  };

  return (
    <div className="card">
      <div className="d-flex flex-column flex-md-row justify-content-between p-2">
        <span className="fw-medium" style={{ fontSize: "1rem" }}>
          Bus Arrival
        </span>
        <div className="d-flex flex-column flex-md-row gap-2">
          {showDatePicker && (
            <div className="flex-fill flex-md-grow-0">
              <DatePicker
                selectsRange
                startDate={
                  param?.start_date ? new Date(param.start_date) : null
                }
                endDate={param?.end_date ? new Date(param.end_date) : null}
                onChange={(update) => {
                  const [start, end] = update;
                  setParam((prev) => ({
                    ...prev,
                    start_date: start ? format(start, "yyyy-MM-dd") : "",
                    end_date: end ? format(end, "yyyy-MM-dd") : "",
                    page: 1,
                  }));
                }}
                isClearable
                placeholderText="Select date range"
                className="form-control small-datepicker-input"
                style={{
                  fontSize: "0.5rem",
                }}
                popperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "beforeWrite",
                      fn({ state }) {
                        state.styles.popper.zIndex = 999;
                      },
                    },
                  ],
                }}
              />
            </div>
          )}
          <div className="flex-fill flex-md-grow-0">
            <Select
              options={dateRangeOptions}
              onChange={handleSelectChange}
              placeholder="Select Period"
              className="react-select"
              classNamePrefix="select"
              value={selectedOption}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 999,
                }),
              }}
            />
          </div>
        </div>
      </div>
      <div className="px-2 pb-2">
        {loading ? (
          <div className="text-center my-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : data?.length == 0 ? (
          <div className="text-center my-2">No Data Found</div>
        ) : error ? (
          <div className="text-center my-3">{error}</div>
        ) : (
          <Chart options={options} series={series} type="bar" height={300} />
        )}
      </div>
    </div>
  );
};

export default BusArrivalCount;
